import { BenchmarkMetric } from "./BenchmarkMetric";

const producers = [
  [
    "deepseek-r1-distill-qwen-32b",
    "106",
    "106/106 (100.00%)",
    "106/106 (100.00%)",
  ],
  ["falcon3-10b-instruct", "113", "113/113 (100.00%)", "113/113 (100.00%)"],
  ["gemini-1.5-pro", "145", "136/145 (93.79%)", "135/145 (93.10%)"],
  ["gemini-2.0-flash", "139", "139/139 (100.00%)", "139/139 (100.00%)"],
  ["gemini-pro", "59", "59/59 (100.00%)", "59/59 (100.00%)"],
  ["gemini-pro-paraphrase", "70", "70/70 (100.00%)", "68/70 (97.14%)"],
  ["gpt-3.5-turbo", "200", "199/200 (99.50%)", "199/200 (99.50%)"],
  ["gpt-4-turbo", "63", "63/63 (100.00%)", "63/63 (100.00%)"],
  ["gpt-4-turbo-paraphrase", "59", "59/59 (100.00%)", "58/59 (98.31%)"],
  ["gpt-4.5-preview", "45", "45/45 (100.00%)", "45/45 (100.00%)"],
  ["gpt-4o", "209", "209/209 (100.00%)", "209/209 (100.00%)"],
  ["gpt-4o-mini", "214", "214/214 (100.00%)", "214/214 (100.00%)"],
  ["llama-2-70b-chat", "65", "65/65 (100.00%)", "65/65 (100.00%)"],
  ["llama-2-7b-chat", "72", "72/72 (100.00%)", "72/72 (100.00%)"],
  ["llama-3.1-8b-instruct", "158", "139/158 (87.97%)", "134/158 (84.81%)"],
  ["llama-3.3-70b-instruct", "54", "54/54 (100.00%)", "53/54 (98.15%)"],
  [
    "ministral-8b-instruct-2410",
    "139",
    "139/139 (100.00%)",
    "139/139 (100.00%)",
  ],
  ["mistral-7b-instruct-v0.2", "68", "68/68 (100.00%)", "68/68 (100.00%)"],
  ["mixtral-8x7b-instruct-v0.1", "70", "66/70 (94.29%)", "64/70 (91.43%)"],
  ["o3-mini", "132", "132/132 (100.00%)", "132/132 (100.00%)"],
  ["qwen1.5-72b-chat-8bit", "63", "63/63 (100.00%)", "63/63 (100.00%)"],
  ["text-bison-002", "69", "69/69 (100.00%)", "68/69 (98.55%)"],
];

function ResultsTable({
  caption,
  headers,
  rows,
}: {
  caption: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div
      className="table-wrap threshold-table"
      role="region"
      aria-label={caption}
      tabIndex={0}
    >
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              <th scope="row">{row[0]}</th>
              {row.slice(1).map((cell, index) => (
                <td key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StylomeLargePanReport() {
  return (
    <details className="benchmark-report">
      <summary>
        <span className="benchmark-identity">
          <span className="metadata-line">
            Stylome 1.2 Large · PAN at CLEF 2025
          </span>
          <strong>PAN 2025</strong>
          <span>
            Human and AI-labeled essays, fiction, and news from an independent
            detection challenge.
          </span>
        </span>
        <span
          className="benchmark-summary-metrics"
          aria-label="Stylome 1.2 Large PAN 2025 highlights"
        >
          <BenchmarkMetric
            label="False-positive rate · 0.01% FPR target"
            value="0%"
            context="0 of 1,277 human passages flagged"
          />
          <BenchmarkMetric
            label="AI recall · 0.01% FPR target"
            value="98.01%"
            context="2,266 of 2,312 AI passages detected"
          />
        </span>
        <span className="benchmark-toggle">Full results</span>
      </summary>
      <div className="benchmark-body">
        <p className="benchmark-context">
          At the 0.01% calibration FPR target selected for the service default,
          Stylome 1.2 Large detected 2,266 of 2,312 AI passages and cleared all
          1,277 human passages.
        </p>
        <p>
          The complete{" "}
          <a
            href="https://pan.webis.de/clef25/pan25-web/generated-content-analysis.html"
            target="_blank"
            rel="noreferrer"
          >
            PAN 2025
          </a>{" "}
          validation set contains 3,589 passages across essays, fiction, and
          news. Both thresholds were fixed before scoring. The percentages below
          distinguish the calibration FPR target from the measured
          false-positive rate on PAN.
        </p>
        <ResultsTable
          caption="Stylome 1.2 Large on PAN 2025 at both operating points"
          headers={[
            "Calibration FPR target",
            "Human false positives",
            "AI detected",
            "AI recall",
            "95% recall interval",
          ]}
          rows={[
            ["0.1%", "0 / 1,277", "2,279 / 2,312", "98.57%", "98.00–99.02%"],
            [
              "0.01% · service default",
              "0 / 1,277",
              "2,266 / 2,312",
              "98.01%",
              "97.35–98.54%",
            ],
          ]}
        />
        <ResultsTable
          caption="PAN 2025 by genre"
          headers={[
            "Genre",
            "Human false positives, both targets",
            "AI recall at 0.1% FPR target",
            "AI recall at 0.01% FPR target",
          ]}
          rows={[
            ["Essays", "0 / 132", "531 / 533 (99.62%)", "531 / 533 (99.62%)"],
            ["Fiction", "0 / 928", "883 / 909 (97.14%)", "876 / 909 (96.37%)"],
            ["News", "0 / 217", "865 / 870 (99.43%)", "859 / 870 (98.74%)"],
          ]}
        />
        <ResultsTable
          caption="PAN 2025 by producer"
          headers={[
            "Producer",
            "AI passages",
            "AI recall at 0.1% FPR target",
            "AI recall at 0.01% FPR target",
          ]}
          rows={producers}
        />
        <p className="fine">
          Producer names and binary labels follow PAN 2025, including its
          separately named paraphrase producers.
        </p>
        <p>
          At the 0.01% FPR target, Llama-3.1-8B accounted for 24 of the 46
          missed AI passages, Gemini-1.5-Pro for 10, and Mixtral-8x7B for 6.
          Fiction accounted for 33 misses.
        </p>
        <h3>Evaluation details</h3>
        <p>
          All 3,589 responses were verified. Twenty passages were shortened to
          the first 10,000 normalized characters to meet the API input limit: 17
          human and 3 AI passages, all classified correctly at both operating
          points. The remaining 3,569 passages were scored in full.
        </p>
        <ResultsTable
          caption="PAN 2025 full-text subset"
          headers={[
            "Calibration FPR target",
            "Human false positives",
            "AI detected",
            "AI recall",
          ]}
          rows={[
            ["0.1%", "0 / 1,260", "2,276 / 2,309", "98.57%"],
            ["0.01%", "0 / 1,260", "2,263 / 2,309", "98.01%"],
          ]}
        />
        <p>
          Scoring completed in 24.4 minutes at 32 concurrent requests, averaging
          1,972 full-request input tokens per second, including the shared
          prefix. Development compute cost was approximately $0.21. There were
          no request errors or checked training, reference, or calibration
          overlaps.
        </p>
        <p className="fine evaluation-note">
          Evaluated September 16, 2026, on the established PAN validation set.
          The exact two-sided 95% interval for the observed human false-positive
          rate is 0–0.2885%, using the independent-passage assumption.
        </p>
      </div>
    </details>
  );
}
